import { ErrorInfo } from 'react';

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  FATAL = 'fatal',
}

/**
 * Error context for additional metadata
 */
export interface ErrorContext {
  component?: string;
  userId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  [key: string]: unknown;
}

/**
 * External error service configuration
 */
export interface ExternalErrorService {
  /** Service name (e.g., 'Sentry', 'LogRocket') */
  name: string;
  /** Send error to the service */
  sendError: (errorData: Record<string, unknown>) => void | Promise<void>;
}

/**
 * Error reporting configuration
 */
interface ErrorReporterConfig {
  enabled: boolean;
  environment: string;
  debug: boolean;
  externalServices: ExternalErrorService[];
}

/**
 * ErrorReporter Service
 *
 * Centralized error reporting utility for the frontend application.
 * Provides a consistent interface for logging and reporting errors
 * to external monitoring services.
 *
 * Features:
 * - Console logging in development
 * - Structured error data
 * - Extensible for monitoring services (Sentry, LogRocket, etc.)
 * - Environment-aware configuration
 *
 * Usage:
 *   errorReporter.reportError(error, ErrorSeverity.ERROR, { component: 'MyComponent' });
 */
class ErrorReporter {
  private config: ErrorReporterConfig;

  constructor() {
    // Use process.env for compatibility with Jest and Vite
    const isDev = process.env.NODE_ENV === 'development';
    const isProd = process.env.NODE_ENV === 'production';

    this.config = {
      enabled: isProd,
      environment: process.env.NODE_ENV || 'development',
      debug: isDev,
      externalServices: [],
    };
  }

  /**
   * Report a generic error
   */
  reportError(
    error: Error | unknown,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    context?: ErrorContext
  ): void {
    if (!this.config.enabled && !this.config.debug) {
      return;
    }

    const errorData = this.buildErrorData(error, severity, context);

    // Log to console in development
    if (this.config.debug) {
      this.logToConsole(errorData, severity);
    }

    // Send to external monitoring services if configured
    // Fire-and-forget to avoid blocking the application
    if (this.config.enabled && this.config.externalServices.length > 0) {
      void this.sendToExternalServices(errorData);
    }
  }

  /**
   * Report a React component error (for ErrorBoundary)
   */
  reportComponentError(error: Error, errorInfo: ErrorInfo, context?: ErrorContext): void {
    const enhancedContext: ErrorContext = {
      ...context,
      componentStack: errorInfo.componentStack,
      reactErrorBoundary: true,
    };

    this.reportError(error, ErrorSeverity.ERROR, enhancedContext);
  }

  /**
   * Report a network/API error
   */
  reportNetworkError(error: Error | unknown, url: string, context?: ErrorContext): void {
    const enhancedContext: ErrorContext = {
      ...context,
      url,
      errorType: 'network',
    };

    this.reportError(error, ErrorSeverity.ERROR, enhancedContext);
  }

  /**
   * Build structured error data
   */
  private buildErrorData(
    error: Error | unknown,
    severity: ErrorSeverity,
    context?: ErrorContext
  ): Record<string, unknown> {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    return {
      message: errorObj.message,
      name: errorObj.name,
      stack: errorObj.stack,
      severity,
      timestamp: new Date().toISOString(),
      environment: this.config.environment,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context,
    };
  }

  /**
   * Log error to console with appropriate level
   */
  private logToConsole(errorData: Record<string, unknown>, severity: ErrorSeverity): void {
    const prefix = `[ErrorReporter ${severity.toUpperCase()}]:`;

    switch (severity) {
      case ErrorSeverity.INFO:
        console.info(prefix, errorData);
        break;
      case ErrorSeverity.WARNING:
        console.warn(prefix, errorData);
        break;
      case ErrorSeverity.ERROR:
      case ErrorSeverity.FATAL:
        console.error(prefix, errorData);
        break;
    }
  }

  /**
   * Send error to all configured external services
   * Uses Promise.allSettled to handle both sync and async services gracefully
   */
  private async sendToExternalServices(errorData: Record<string, unknown>): Promise<void> {
    // Use Promise.allSettled to ensure all services are attempted even if some fail
    const results = await Promise.allSettled(
      this.config.externalServices.map(async (service) => {
        try {
          // Handle both sync and async sendError implementations
          await service.sendError(errorData);
        } catch (serviceError) {
          // Re-throw to be caught by Promise.allSettled
          throw new Error(
            `Failed to send error to ${service.name}: ${serviceError instanceof Error ? serviceError.message : String(serviceError)}`
          );
        }
      })
    );

    // Log failures in debug mode
    if (this.config.debug) {
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const service = this.config.externalServices[index];
          const serviceName = service?.name ?? 'Unknown Service';
          console.error(`[ErrorReporter]: Failed to send error to ${serviceName}:`, result.reason);
        }
      });
    }
  }

  /**
   * Add an external error monitoring service
   * @param service External error service configuration
   */
  addService(service: ExternalErrorService): void {
    if (!this.config.externalServices.find((s) => s.name === service.name)) {
      this.config.externalServices.push(service);
    }
  }

  /**
   * Remove an external error monitoring service by name
   * @param serviceName Name of the service to remove
   */
  removeService(serviceName: string): void {
    this.config.externalServices = this.config.externalServices.filter(
      (s) => s.name !== serviceName
    );
  }

  /**
   * Update configuration
   */
  configure(config: Partial<ErrorReporterConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Export singleton instance
export const errorReporter = new ErrorReporter();
